import moment from "moment";

const URL_REGEX = /(https?:\/\/[^\s]+)/g;
const USER_MENTION_REGEX = /@\[__([^_]+)__\]\(__([^_]+)__\)/g;
const ROOM_MENTION_REGEX = /#\[__([^)]+)__\]\(__([^)]+)__\)/g;
const NUMBER_REGEX = /^[0-9]+$/;
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const URL_REGEX_WITHOUT_PROTOCOL = /^(https?:\/\/)?(www\.)?/g;
const DBOT_LINK_REGEX = /\[([^\]]+)\]\(([^)]+)\)/;

const convertUUIDToInt = (uuid: string): bigint | null => {
  const hex = uuid.replace(/-/g, "");
  if (hex.length !== 32) {
    // Invalid UUID format
    return null;
  }
  const high = BigInt(`0x${hex.slice(0, 16)}`);
  const low = BigInt(`0x${hex.slice(16)}`);
  return (high << BigInt(64)) + low;
};

const convertIntToUUID = (integer: bigint): string | null => {
  const hexString = integer.toString(16).padStart(32, "0");
  if (hexString.length !== 32) {
    // Invalid integer format
    return null;
  }
  const uuid = `${hexString.slice(0, 8)}-${hexString.slice(
    8,
    12
  )}-${hexString.slice(12, 16)}-${hexString.slice(16, 20)}-${hexString.slice(
    20
  )}`;

  return uuid;
};

/**
 * Extracts URL from content, prioritizing markdown link format [text](url)
 * Falls back to regular URL detection if no markdown link is found
 */
const extractUrlFromContent = (content: string): string | null => {
  try {
    if (!content || typeof content !== "string") return null;

    let url: string | null = null;

    // First, try to extract URL from Markdown link format [text](url)
    // Use a new regex instance to avoid global state issues
    const markdownMatch = content.match(DBOT_LINK_REGEX);
    if (markdownMatch && markdownMatch.length >= 3) {
      // Extract URL from the second group (inside parentheses)
      url = markdownMatch[2]?.trim() || null;
    }

    // If no markdown link found, try regular URL detection
    if (url == null) {
      const urlMatch = content.match(URL_REGEX);
      if (urlMatch && urlMatch.length > 0) {
        url = urlMatch[0];
      }
    }

    return url;
  } catch (error) {
    console.error("Error extracting URL from content", error);
    return null;
  }
};

const messageShorten: any = (text: string, num: number) => {
  if (!text) return "";
  if (!num) num = 128;
  if (num >= text.length) return text;
  const prefix = text.slice(0, num);
  return `${prefix}...`;
};

const emojis = [
  { label: "like", symbol: "👍" },
  { label: "dislike", symbol: "👎" },
  { label: "pray", symbol: "🙏" },
  { label: "heart", symbol: "❤️" },
  { label: "laugh", symbol: "😆" },
  { label: "bow", symbol: "🙇‍♂️" },
  { label: "cry", symbol: "😭" },
];

const getSymbolByEmojiName = (emoji: string): string => {
  switch (emoji) {
    case "like":
      return "👍";
    case "dislike":
      return "👎";
    case "pray":
      return "🙏";
    case "heart":
      return "❤️";
    case "laugh":
      return "😆";
    case "bow":
      return "🙇‍♂️";
    case "cry":
      return "😭";
    default:
      return "";
  }
};

const isDateToday = (dateTimestamp: string): boolean => {
  const date = moment(dateTimestamp).startOf("day");
  const today = moment().startOf("day");
  return date.isSame(today, "day");
};

const isDateYesterday = (dateTimestamp: string): boolean => {
  const date = moment(dateTimestamp).startOf("day");
  const yesterday = moment().subtract(1, "day").startOf("day");
  return date.isSame(yesterday, "day");
};

const getTimeInMessageHistory = (
  currentDateTimestamp: string,
  oldDateTimestamp?: string,
  todayText: string = "Today",
  yesterdayText: string = "Yesterday"
): string => {
  const dateFromCurrentDateTimestamp =
    moment(currentDateTimestamp).startOf("day");
  const dateFromOldDateTimestamp = moment(oldDateTimestamp).startOf("day");
  const dateString = dateFromCurrentDateTimestamp.format("YYYY-MM-DD");

  const isToday = isDateToday(currentDateTimestamp);
  const isYesterday = isDateYesterday(currentDateTimestamp);

  if (
    !oldDateTimestamp ||
    !dateFromCurrentDateTimestamp.isSame(dateFromOldDateTimestamp, "day")
  ) {
    if (isToday) {
      return todayText;
    } else if (isYesterday) {
      return yesterdayText;
    } else {
      return dateString;
    }
  }
  return "";
};
const checkTokenAddress = (value: string): boolean => {
  if (!value) return false;
  const tokenAddressPattern = /^(0x[a-fA-F0-9]{40})$/;
  return tokenAddressPattern.test(value);
};

const convertUUIDToBytes16 = (uuid: string): `0x${string}` | null => {
  if (!uuid) return null;
  const uuidWithoutHyphens = uuid.replace(/-/g, "");
  const bytes16Uuid = `0x${uuidWithoutHyphens}`;
  return bytes16Uuid as `0x${string}`;
};

const normalizeDisabledChatAddresses = (addresses: unknown): string[] => {
  if (!Array.isArray(addresses)) return [];

  return addresses
    .filter(
      (address: unknown): address is string => typeof address === "string"
    )
    .map((address) => address.trim().toLowerCase())
    .filter(Boolean);
};

const isChatAddressDisabled = (
  address: string | null | undefined,
  disabledAddresses: readonly string[]
): boolean =>
  !!address && disabledAddresses.includes(address.trim().toLowerCase());

export {
  normalizeDisabledChatAddresses,
  isChatAddressDisabled,
  messageShorten,
  URL_REGEX,
  NUMBER_REGEX,
  USER_MENTION_REGEX,
  ROOM_MENTION_REGEX,
  UUID_REGEX,
  URL_REGEX_WITHOUT_PROTOCOL,
  DBOT_LINK_REGEX,
  emojis,
  convertUUIDToInt,
  convertIntToUUID,
  extractUrlFromContent,
  getSymbolByEmojiName,
  isDateToday,
  getTimeInMessageHistory,
  checkTokenAddress,
  convertUUIDToBytes16,
};
